import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Gauge, Battery, MapPin, Phone, Euro, CheckCircle2, XCircle, User, Mail, MessageSquare, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    loadListing();
    checkUser();
  }, [id]);

  const checkUser = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (res.ok) {
        const userData = await res.json();
        setCurrentUser(userData);
      }
    } catch (error) {
      // User not logged in
    }
  };

  const loadListing = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/listings/${id}`);
      if (!res.ok) throw new Error('Listing not found');
      const data = await res.json();
      setListing(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load listing:', error);
      setLoading(false);
    }
  };

  const calculateNetPrice = (price) => {
    return (price / 1.23).toFixed(2);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.email && !contactForm.phone) {
      toast.error('Prosím zadajte email alebo telefónne číslo');
      return;
    }
    
    setSendingMessage(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/listings/${id}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (!res.ok) throw new Error('Failed to send message');
      
      toast.success('Správa bola odoslaná predajcovi');
      setContactDialogOpen(false);
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Nepodarilo sa odoslať správu');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Inzerát sa nenašiel</h2>
            <Button onClick={() => navigate('/')}>Späť na hlavnú stránku</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            variant="outline"
            className="mb-6"
          >
            ← Späť
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden" data-testid="listing-image">
                {listing.images && listing.images.length > 0 ? (
                  <img
                    src={listing.images[0]}
                    alt={`${listing.brand_name} ${listing.model_name}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Battery className="h-24 w-24 text-slate-300" />
                  </div>
                )}
              </div>

              <Card className="p-6">
                <h1 className="text-3xl font-bold text-primary mb-2" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="listing-title">
                  {listing.brand_name} {listing.model_name}
                </h1>
                <div className="flex items-center gap-2 mb-4">
                  {listing.sms_verified ? (
                    <Badge className="bg-green-100 text-green-800" data-testid="verified-badge">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Overené
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-800" data-testid="unverified-badge">
                      <XCircle className="h-3 w-3 mr-1" />
                      Ne overené
                    </Badge>
                  )}
                  {listing.promoted_until && new Date(listing.promoted_until) > new Date() && (
                    <Badge className="bg-accent text-white" data-testid="promoted-badge">
                      Propagované
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="h-5 w-5" />
                    <div>
                      <p className="text-xs text-slate-500">Rok</p>
                      <p className="font-semibold" data-testid="listing-year">{listing.year}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Gauge className="h-5 w-5" />
                    <div>
                      <p className="text-xs text-slate-500">Najazdené</p>
                      <p className="font-semibold" data-testid="listing-mileage">{listing.mileage.toLocaleString()} km</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Battery className="h-5 w-5" />
                    <div>
                      <p className="text-xs text-slate-500">Výkon</p>
                      <p className="font-semibold" data-testid="listing-power">{listing.power} kW</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="h-5 w-5" />
                    <div>
                      <p className="text-xs text-slate-500">Karoséria</p>
                      <p className="font-semibold" data-testid="listing-body-type">{listing.body_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <div>
                      <p className="text-xs text-slate-500">Pohon</p>
                      <p className="font-semibold" data-testid="listing-drive-type">{listing.drive_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Battery className="h-5 w-5" />
                    <div>
                      <p className="text-xs text-slate-500">Batéria</p>
                      <p className="font-semibold" data-testid="listing-battery">{listing.battery_included ? 'S batériou' : 'Bez batérie'}</p>
                    </div>
                  </div>
                </div>

                {listing.description && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-primary mb-2" style={{fontFamily: 'Manrope, sans-serif'}}>Popis</h3>
                    <p className="text-slate-600" data-testid="listing-description">{listing.description}</p>
                  </div>
                )}
              </Card>
            </div>

            <div className="lg:col-span-1">
              <Card className="p-6 sticky top-20">
                <div className="mb-6">
                  <p className="text-sm text-slate-500 mb-1">Cena</p>
                  <p className="text-3xl font-bold text-accent" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="listing-price">
                    €{listing.price.toLocaleString()}
                  </p>
                  {listing.vat_deductible && (
                    <p className="text-sm text-slate-600 mt-1" data-testid="net-price">
                      Neto: €{calculateNetPrice(listing.price)} (bez 23% DPH)
                    </p>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  <h3 className="text-lg font-semibold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>Predajca</h3>
                  
                  {listing.seller_name && (
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-xs text-slate-500">Meno</p>
                        <p className="font-semibold" data-testid="seller-name">{listing.seller_name}</p>
                      </div>
                    </div>
                  )}
                  
                  {listing.city && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-xs text-slate-500">Lokalita</p>
                        <p className="font-semibold" data-testid="listing-city">{listing.city}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    {currentUser ? (
                      <Phone className="h-5 w-5 text-primary" />
                    ) : (
                      <Lock className="h-5 w-5 text-slate-400" />
                    )}
                    <div>
                      <p className="text-xs text-slate-500">Telefón</p>
                      {currentUser ? (
                        <p className="font-semibold" data-testid="listing-phone">{listing.phone}</p>
                      ) : (
                        <p className="text-sm text-slate-500 italic" data-testid="listing-phone-hidden">číslo sa zobrazí po prihlásení</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {currentUser ? (
                    <Button
                      data-testid="call-seller-button"
                      className="w-full bg-primary text-white hover:bg-slate-800 font-semibold h-12"
                      onClick={() => window.location.href = `tel:${listing.phone}`}
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Zavolať
                    </Button>
                  ) : (
                    <Button
                      data-testid="login-to-call-button"
                      className="w-full bg-slate-200 text-slate-600 hover:bg-slate-300 font-semibold h-12"
                      onClick={() => navigate('/login')}
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      Prihláste sa pre zobrazenie telefónu
                    </Button>
                  )}
                  
                  <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid="contact-form-button"
                        variant="outline"
                        className="w-full font-semibold h-12"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Napísať správu
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="contact-dialog">
                      <DialogHeader>
                        <DialogTitle>Kontaktovať predajcu</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleContactSubmit} className="space-y-4">
                        <div>
                          <Label className="text-sm font-medium">Vaše meno *</Label>
                          <Input
                            data-testid="contact-name-input"
                            value={contactForm.name}
                            onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Váš email</Label>
                          <Input
                            data-testid="contact-email-input"
                            type="email"
                            value={contactForm.email}
                            onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Váš telefón</Label>
                          <Input
                            data-testid="contact-phone-input"
                            type="tel"
                            value={contactForm.phone}
                            onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                          />
                        </div>
                        <p className="text-xs text-slate-500">* Zadajte email alebo telefón, aby vás predajca mohol kontaktovať</p>
                        <div>
                          <Label className="text-sm font-medium">Správa *</Label>
                          <Textarea
                            data-testid="contact-message-input"
                            rows={4}
                            value={contactForm.message}
                            onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                            placeholder="Dobrý deň, mám záujem o toto vozidlo..."
                            required
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setContactDialogOpen(false)}
                            disabled={sendingMessage}
                          >
                            Zrušiť
                          </Button>
                          <Button
                            data-testid="send-message-button"
                            type="submit"
                            className="flex-1 bg-primary text-white hover:bg-slate-800"
                            disabled={sendingMessage}
                          >
                            {sendingMessage ? 'Odosielam...' : 'Odoslať správu'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ListingDetailPage;
